import { Component } from '@theme/component';

const MOBILE_MEDIA_QUERY = '(width < 64rem)';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Drag/swipe state
   */
  #dragStartX = null;
  #dragCurrentX = null;
  #isDragging = false;

  /**
   * Document-level drag handlers (bound for removal)
   */
  #handleMove = null;
  #handleEnd = null;

  /**
   * @type {MediaQueryList|undefined}
   */
  #mobileMediaQuery = undefined;

  /**
   * @type {ResizeObserver|undefined}
   */
  #resizeObserver = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.#mobileMediaQuery.addEventListener('change', this.#handleBreakpointChange);

    this.#initializeComponent();
  }

  /**
   * Initializes the component once refs are ready
   */
  #initializeComponent() {
    if (this.refs.slides?.length > 0) {
      this.#setupComponent();
      return;
    }

    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      if (!this.refs.slides?.length) {
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.#setupComponent();
        });
        return;
      }

      this.#setupComponent();
    });
  }

  /**
   * Slides visible at the current breakpoint.
   * Mobile-only slides are excluded on desktop.
   * @returns {HTMLElement[]}
   */
  #getVisibleSlides() {
    const slides = Array.isArray(this.refs.slides) ? this.refs.slides : [];
    const isMobile = this.#isMobile();

    return slides.filter((slide) => {
      const isMobileOnly = slide.classList.contains('announcement-bar__slide--mobile-only')
        || slide.hasAttribute('data-mobile-only');

      if (!isMobileOnly) return true;

      return isMobile;
    });
  }

  /**
   * Pagination lives beside the slider in `.announcement-bar__center`.
   * @returns {HTMLElement|null}
   */
  #getPagination() {
    return this.closest('.announcement-bar__center')
      ?.querySelector('.announcement-bar__pagination')
      ?? this.querySelector('.announcement-bar__pagination');
  }

  /**
   * Pagination dots for the current breakpoint (excludes mobile-only on desktop).
   * @returns {HTMLElement[]}
   */
  #getVisiblePaginationDots() {
    const pagination = this.#getPagination();
    const dots = pagination
      ? [...pagination.querySelectorAll('.announcement-bar__pagination-dot')]
      : [];
    const isMobile = this.#isMobile();

    return dots.filter((dot) => {
      if (dot.hidden) return false;

      const isMobileOnly = dot.classList.contains('announcement-bar__pagination-dot--mobile-only')
        || dot.hasAttribute('data-mobile-only');

      if (!isMobileOnly) return true;

      return isMobile;
    });
  }

  #isMobile() {
    return this.#mobileMediaQuery?.matches
      ?? window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  }

  /**
   * Sets up the component once refs are guaranteed to be ready
   */
  #setupComponent() {
    this.#reinitForBreakpoint();

    const pagination = this.#getPagination();
    const paginationDots = pagination
      ?.querySelectorAll('.announcement-bar__pagination-dot');

    paginationDots?.forEach((dot) => {
      dot.addEventListener('click', () => {
        const visibleDots = this.#getVisiblePaginationDots();
        const visibleIndex = visibleDots.indexOf(dot);

        if (visibleIndex < 0) return;

        this.current = visibleIndex;
        this.pause();
        this.paused = false;
        this.play();
      });
    });

    this.#setupDragHandlers();

    // Re-sync after layout — mobile-only slides may still be resolving display.
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#reinitForBreakpoint();
    });

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => {
      this.#syncSlideLayout();
      this.#syncPagination();
      this.#updateSlidePosition();
    });
    this.#resizeObserver.observe(this);
  }

  /**
   * Match pagination dots to slides, then show only dots for the current breakpoint.
   */
  #syncPagination() {
    const pagination = this.#getPagination();
    if (!pagination) return;

    const allSlides = Array.isArray(this.refs.slides) ? this.refs.slides : [];
    const visibleSlides = this.#getVisibleSlides();
    const dots = [...pagination.querySelectorAll('.announcement-bar__pagination-dot')];

    dots.forEach((dot, index) => {
      const slide = allSlides[index];

      if (!slide) {
        dot.hidden = true;
        return;
      }

      const isMobileOnly = slide.classList.contains('announcement-bar__slide--mobile-only')
        || slide.hasAttribute('data-mobile-only');

      dot.classList.toggle('announcement-bar__pagination-dot--mobile-only', isMobileOnly);
      dot.toggleAttribute('data-mobile-only', isMobileOnly);
      dot.hidden = !visibleSlides.includes(slide);
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
    });

    // Hide the whole control when only one slide is active at this breakpoint
    pagination.hidden = visibleSlides.length <= 1;
  }

  /**
   * Full slider reinit for the active breakpoint (desktop vs mobile slide set).
   */
  #reinitForBreakpoint() {
    this.#current = 0;
    this.#syncSlideLayout();
    this.#syncPagination();
    this.#updateSlidePosition();
    this.pause();
    this.paused = false;
    this.play();
  }

  /**
   * Track is N * 100% of the slider. Each visible slide is (100 / N)% of the
   * track (= one slider viewport). Hidden mobile-only slides are display:none.
   */
  #syncSlideLayout() {
    const slidesContainer = this.querySelector('.announcement-bar__slides');
    const allSlides = Array.isArray(this.refs.slides) ? this.refs.slides : [];
    const visibleSlides = this.#getVisibleSlides();
    const slideCount = visibleSlides.length;

    if (!slidesContainer || slideCount === 0) return;

    const slideBasis = `${100 / slideCount}%`;

    allSlides.forEach((slide) => {
      const isVisible = visibleSlides.includes(slide);

      if (isVisible) {
        slide.style.display = '';
        slide.style.flexBasis = slideBasis;
        slide.style.width = slideBasis;
        slide.style.minWidth = slideBasis;
      } else {
        slide.style.display = 'none';
        slide.style.flexBasis = '';
        slide.style.width = '';
        slide.style.minWidth = '';
      }
    });

    slidesContainer.style.width = `${slideCount * 100}%`;

    if (this.#current >= slideCount) {
      this.#current = Math.max(0, slideCount - 1);
    }
  }

  #handleBreakpointChange = () => {
    // Let CSS display rules apply before measuring
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#reinitForBreakpoint();
    });
  };

  /**
   * Setup drag and swipe handlers for touch and mouse
   */
  #setupDragHandlers() {
    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (!slidesContainer) return;

    const handleStart = (e) => {
      const link = e.target.closest('a');
      if (link) {
        link.dataset.preventClick = 'false';
      }

      this.#isDragging = true;
      this.#dragStartX = this.#getPointerX(e);
      this.#dragCurrentX = this.#dragStartX;
      slidesContainer.style.transition = 'none';
      this.suspend();

      this.#handleMove = (moveEvent) => {
        if (!this.#isDragging) return;

        const deltaX = Math.abs(this.#getPointerX(moveEvent) - this.#dragStartX);
        if (deltaX > 5) {
          const link = moveEvent.target.closest('a');
          if (link) {
            link.dataset.preventClick = 'true';
          }
        }

        this.#dragCurrentX = this.#getPointerX(moveEvent);
        const moveDeltaX = this.#dragCurrentX - this.#dragStartX;

        const slideCount = this.#getVisibleSlides().length;
        if (!slidesContainer || !slideCount) return;

        const sliderWidth = slidesContainer.parentElement.offsetWidth;
        const slideWidth = sliderWidth;
        const currentNormalizedIndex = this.#current;
        const currentTranslate = -currentNormalizedIndex * slideWidth;
        const newTranslate = currentTranslate + moveDeltaX;

        const minTranslate = -(slideCount - 1) * slideWidth;
        const maxTranslate = 0;
        const constrainedTranslate = Math.max(
          minTranslate,
          Math.min(maxTranslate, newTranslate),
        );

        slidesContainer.style.translate = `${constrainedTranslate}px 0`;
      };

      this.#handleEnd = (endEvent) => {
        if (!this.#isDragging) return;
        if (!slidesContainer) return;

        const deltaX = this.#dragCurrentX - this.#dragStartX;
        const threshold = 50;
        const wasDrag = Math.abs(deltaX) > threshold;

        this.#isDragging = false;
        this.#removeDocumentHandlers();
        slidesContainer.style.transition = '';

        if (wasDrag) {
          const link = endEvent?.target?.closest('a');
          if (link) {
            link.dataset.preventClick = 'true';
          }

          if (deltaX > 0) {
            this.previous();
          } else {
            this.next();
          }
        } else {
          this.#updateSlidePosition();
        }

        this.#dragStartX = null;
        this.#dragCurrentX = null;
        this.resume();
      };

      document.addEventListener('mousemove', this.#handleMove);
      document.addEventListener('mouseup', this.#handleEnd);
      document.addEventListener('touchmove', this.#handleMove, { passive: true });
      document.addEventListener('touchend', this.#handleEnd);
    };

    slidesContainer.addEventListener('mousedown', handleStart);
    slidesContainer.addEventListener('touchstart', handleStart, { passive: true });

    slidesContainer.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link && link.dataset.preventClick === 'true') {
        e.preventDefault();
        delete link.dataset.preventClick;
      }
    });
  }

  #removeDocumentHandlers() {
    if (this.#handleMove) {
      document.removeEventListener('mousemove', this.#handleMove);
      document.removeEventListener('touchmove', this.#handleMove);
      this.#handleMove = null;
    }
    if (this.#handleEnd) {
      document.removeEventListener('mouseup', this.#handleEnd);
      document.removeEventListener('touchend', this.#handleEnd);
      this.#handleEnd = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.#removeDocumentHandlers();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#mobileMediaQuery?.removeEventListener(
      'change',
      this.#handleBreakpointChange,
    );
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
  }

  #getPointerX(e) {
    return e.touches ? e.touches[0].clientX : e.clientX;
  }

  next() {
    const slideCount = this.#getVisibleSlides().length;
    if (slideCount === 0) return;

    this.#current = (this.#current + 1) % slideCount;
    this.#updateSlidePosition();
  }

  previous() {
    const slideCount = this.#getVisibleSlides().length;
    if (slideCount === 0) return;

    this.#current = (this.#current - 1 + slideCount) % slideCount;
    this.#updateSlidePosition();
  }

  #updateSlidePosition() {
    const visibleSlides = this.#getVisibleSlides();
    const slideCount = visibleSlides.length;
    if (slideCount === 0) return;

    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (!slidesContainer) return;

    if (!this.#isDragging) {
      // Track is slideCount * 100% wide; move by one slide = 100/slideCount % of track
      const translateX = -(this.#current * 100) / slideCount;
      slidesContainer.style.translate = `${translateX}% 0`;
    }

    visibleSlides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== this.#current}`);
    });

    const paginationDots = this.#getVisiblePaginationDots();
    paginationDots.forEach((dot, index) => {
      const isActive = index === this.#current;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', `${isActive}`);
    });
  }

  /**
   * @param {number} [interval]
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    const slideCount = this.#getVisibleSlides().length;
    if (slideCount === 0 || slideCount === 1) return;

    this.paused = false;
    this.suspend();

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slideCount = this.#getVisibleSlides().length;
    if (slideCount === 0) return;

    let normalizedIndex = current % slideCount;
    if (normalizedIndex < 0) {
      normalizedIndex += slideCount;
    }

    this.#current = normalizedIndex;
    this.#updateSlidePosition();
  }

  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}

/**
 * Desktop collection links dropdown under the announcement bar.
 */
class AnnouncementBarCollections {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.toggles = [
      ...root.querySelectorAll('[data-announcement-collections-toggle]'),
    ];
    this.panel = root.querySelector('[data-announcement-collections-panel]');
    this.#lastToggle = null;

    if (this.toggles.length === 0 || !this.panel) return;

    this.toggles.forEach((toggle) => {
      toggle.addEventListener('click', this.#handleToggleClick);
    });
    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('keydown', this.#handleKeyDown);
  }

  /** @type {HTMLElement | null} */
  #lastToggle;

  get isOpen() {
    return this.toggles.some(
      (toggle) => toggle.getAttribute('aria-expanded') === 'true',
    );
  }

  /**
   * @param {boolean} open
   */
  setOpen(open) {
    if (this.toggles.length === 0 || !this.panel) return;

    this.toggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', String(open));
    });
    this.panel.hidden = !open;
    this.root.classList.toggle('announcement-bar--collections-open', open);
  }

  #handleToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#lastToggle = event.currentTarget;
    this.setOpen(!this.isOpen);
  };

  #handleDocumentClick = (event) => {
    if (!this.isOpen) return;
    if (this.root.contains(event.target)) return;

    this.setOpen(false);
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    this.setOpen(false);
    (this.#lastToggle ?? this.toggles[0])?.focus();
  };
}

/**
 * Mobile announcement details dropdown under the announcement bar.
 * Shows only the menu related to the announcement whose toggle was opened.
 */
class AnnouncementBarMobilePanel {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.toggles = [
      ...root.querySelectorAll('[data-announcement-mobile-toggle]'),
    ];
    this.panel = root.querySelector('[data-announcement-mobile-panel]');
    this.list = root.querySelector('[data-announcement-mobile-list]');
    this.slider = root.querySelector('announcement-bar-component');
    this.#mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.#lastToggle = null;

    if (this.toggles.length === 0 || !this.panel || !this.list) return;

    this.toggles.forEach((toggle) => {
      toggle.addEventListener('click', this.#handleToggleClick);
    });

    root.querySelectorAll('.announcement-bar__pagination-dot').forEach((dot) => {
      dot.addEventListener('click', this.#handleSlideChange);
    });

    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('keydown', this.#handleKeyDown);
    this.#mobileMediaQuery.addEventListener('change', this.#handleBreakpointChange);
  }

  /** @type {MediaQueryList} */
  #mobileMediaQuery;

  /** @type {HTMLElement | null} */
  #lastToggle;

  get isOpen() {
    return this.#lastToggle?.getAttribute('aria-expanded') === 'true';
  }

  /**
   * @param {HTMLTemplateElement} template
   */
  #renderMenu(template) {
    this.list.replaceChildren();
    this.list.appendChild(template.content.cloneNode(true));
  }

  /**
   * @param {HTMLElement} toggle
   * @returns {HTMLTemplateElement | null}
   */
  #getTemplateForToggle(toggle) {
    const slide = toggle.closest('slideshow-slide');
    const template = slide?.querySelector(
      'template[data-announcement-mobile-panel-item]',
    );

    if (!(template instanceof HTMLTemplateElement)) return null;

    return template;
  }

  /**
   * @param {boolean} open
   * @param {HTMLElement | null} [activeToggle]
   */
  setOpen(open, activeToggle = this.#lastToggle) {
    if (this.toggles.length === 0 || !this.panel) return;

    this.toggles.forEach((toggle) => {
      const isExpanded = open && toggle === activeToggle;
      toggle.setAttribute('aria-expanded', String(isExpanded));
    });

    this.panel.hidden = !open;
    this.root.classList.toggle('announcement-bar--mobile-open', open);

    if (!open) {
      this.list?.replaceChildren();
    }

    if (!(this.slider instanceof AnnouncementBar)) return;

    if (open) {
      this.slider.pause();
      return;
    }

    this.slider.paused = false;
    this.slider.play();
  }

  #handleToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const toggle = event.currentTarget;

    if (!(toggle instanceof HTMLElement)) return;

    const template = this.#getTemplateForToggle(toggle);

    if (!template) return;

    const isClosing = this.isOpen && this.#lastToggle === toggle;

    if (isClosing) {
      this.setOpen(false);
      this.#lastToggle = null;
      return;
    }

    this.#renderMenu(template);
    this.#lastToggle = toggle;
    this.setOpen(true, toggle);
  };

  #handleSlideChange = () => {
    if (!this.isOpen) return;

    this.setOpen(false);
    this.#lastToggle = null;
  };

  #handleDocumentClick = (event) => {
    if (!this.isOpen) return;
    if (this.root.contains(event.target)) return;

    this.setOpen(false);
    this.#lastToggle = null;
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    const toggle = this.#lastToggle ?? this.toggles[0];
    this.setOpen(false);
    this.#lastToggle = null;
    toggle?.focus();
  };

  #handleBreakpointChange = () => {
    if (this.#mobileMediaQuery.matches) return;

    this.setOpen(false);
    this.#lastToggle = null;
  };
}

/**
 * Desktop announcement bar menus: hover/focus mega + simple dropdowns.
 */
class AnnouncementBarMenus {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.items = [...root.querySelectorAll('[data-announcement-menu-item]')];
    this.#closeTimeout = null;
    this.#openItem = null;

    if (this.items.length === 0) return;

    this.items.forEach((item) => {
      item.addEventListener('mouseenter', this.#handleItemEnter);
      item.addEventListener('mouseleave', this.#handleItemLeave);
      item.addEventListener('focusin', this.#handleItemEnter);
      item.addEventListener('focusout', this.#handleItemFocusOut);
    });

    document.addEventListener('keydown', this.#handleKeyDown);
    document.addEventListener('click', this.#handleDocumentClick);
  }

  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimeout;

  /** @type {HTMLElement | null} */
  #openItem;

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #getMenuItemFromTarget(target) {
    if (!(target instanceof Element)) return null;

    const item = target.closest('[data-announcement-menu-item]');

    if (!(item instanceof HTMLElement)) return null;
    if (!this.items.includes(item)) return null;

    return item;
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} open
   */
  #setItemOpen(item, open) {
    const trigger = item.querySelector('[data-announcement-menu-trigger]');
    const panel = item.querySelector('[data-announcement-menu-panel]');

    if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) {
      return;
    }

    item.classList.toggle('is-open', open);
    trigger.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
  }

  /**
   * @param {HTMLElement | null} item
   */
  #open(item) {
    if (this.#closeTimeout) {
      clearTimeout(this.#closeTimeout);
      this.#closeTimeout = null;
    }

    if (!item) return;
    if (this.#openItem === item) return;

    this.items.forEach((menuItem) => {
      this.#setItemOpen(menuItem, menuItem === item);
    });

    this.#openItem = item;
    this.root.classList.add('announcement-bar--menu-open');
  }

  #closeAll() {
    if (this.#closeTimeout) {
      clearTimeout(this.#closeTimeout);
      this.#closeTimeout = null;
    }

    this.items.forEach((item) => this.#setItemOpen(item, false));
    this.#openItem = null;
    this.root.classList.remove('announcement-bar--menu-open');
  }

  /**
   * @param {HTMLElement} item
   */
  #scheduleClose(item) {
    if (this.#closeTimeout) {
      clearTimeout(this.#closeTimeout);
    }

    this.#closeTimeout = setTimeout(() => {
      if (this.#openItem !== item) return;
      this.#closeAll();
    }, 100);
  }

  #handleItemEnter = (event) => {
    const item = event.currentTarget;

    if (!(item instanceof HTMLElement)) return;

    this.#open(item);
  };

  #handleItemLeave = (event) => {
    const item = event.currentTarget;

    if (!(item instanceof HTMLElement)) return;

    const nextItem = this.#getMenuItemFromTarget(event.relatedTarget);

    if (nextItem && nextItem !== item) {
      this.#open(nextItem);
      return;
    }

    this.#scheduleClose(item);
  };

  #handleItemFocusOut = (event) => {
    const item = event.currentTarget;

    if (!(item instanceof HTMLElement)) return;

    const nextItem = this.#getMenuItemFromTarget(event.relatedTarget);

    if (nextItem && nextItem !== item) {
      this.#open(nextItem);
      return;
    }

    if (item.contains(event.relatedTarget)) return;

    this.#scheduleClose(item);
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#openItem) return;

    const trigger = this.#openItem.querySelector(
      '[data-announcement-menu-trigger]',
    );

    this.#closeAll();
    if (trigger instanceof HTMLElement) {
      trigger.focus();
    }
  };

  #handleDocumentClick = (event) => {
    if (!this.#openItem) return;
    if (this.root.contains(event.target)) return;

    this.#closeAll();
  };
}

function initAnnouncementBarCollections() {
  document.querySelectorAll('[data-announcement-collections]').forEach((root) => {
    if (root.dataset.collectionsInitialised === 'true') return;

    root.dataset.collectionsInitialised = 'true';
    new AnnouncementBarCollections(root);
  });
}

function initAnnouncementBarMenus() {
  document.querySelectorAll('.announcement-bar').forEach((root) => {
    if (root.dataset.menusInitialised === 'true') return;

    const menuItems = root.querySelectorAll('[data-announcement-menu-item]');

    if (menuItems.length === 0) return;

    root.dataset.menusInitialised = 'true';
    new AnnouncementBarMenus(root);
  });
}

function initAnnouncementBarMobilePanels() {
  document.querySelectorAll('.announcement-bar').forEach((root) => {
    if (root.dataset.mobileInitialised === 'true') return;

    const list = root.querySelector('[data-announcement-mobile-list]');
    const panel = root.querySelector('[data-announcement-mobile-panel]');

    if (!list || !panel) return;

    const templates = root.querySelectorAll(
      'template[data-announcement-mobile-panel-item]',
    );

    if (templates.length === 0) {
      root.querySelectorAll('[data-announcement-mobile-toggle]').forEach((toggle) => {
        toggle.remove();
      });
      panel.remove();
      root.dataset.mobileInitialised = 'true';
      return;
    }

    root.querySelectorAll('[data-announcement-mobile-toggle]').forEach((toggle) => {
      const slide = toggle.closest('slideshow-slide');
      const hasTemplate = Boolean(
        slide?.querySelector('template[data-announcement-mobile-panel-item]'),
      );

      if (hasTemplate) {
        toggle.hidden = false;
        return;
      }

      toggle.remove();
    });

    root.classList.add('announcement-bar--has-mobile-announcements');
    root.setAttribute('data-announcement-mobile', '');
    root.dataset.mobileInitialised = 'true';
    new AnnouncementBarMobilePanel(root);
  });
}

function initAnnouncementBarExtras() {
  initAnnouncementBarCollections();
  initAnnouncementBarMenus();
  initAnnouncementBarMobilePanels();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAnnouncementBarExtras, {
    once: true,
  });
} else {
  initAnnouncementBarExtras();
}

document.addEventListener('shopify:section:load', initAnnouncementBarExtras);
